import {ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {keyboardKeys} from '../../constants/keyboard-keys.constants';
import {NgModel} from '@angular/forms';
import {ValidationService} from '../../services/validation.service';
import {FieldService} from '../../services/field.service';

@Component({
  selector: 'si-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InputComponent {
  @Input() text: string;
  @Input() placeholder: string;
  @Input() styleClass: string;
  @Input() id: string;
  @Input() name: string;
  @Input() disabled: boolean;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() rule: string;
  @Input() validationExternalWarningText: string;

  //Error message to display when the input model does not validate the ruleRegex input.
  @Input() ruleRegexErrorText: string;
  @Input() min: number;
  @Input() max: number;
  @Input() autoFocus: boolean;
  @Input() model: any;
  @Input() hideLabel = false;
  @Input() inputIcon: string;
  @Input() updateNgModelOnBlur: boolean;
  @Output() modelChange = new EventEmitter<any>();
  @Output() keyEnterPress = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  @ViewChild('formModel') formModel: NgModel;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;
  capsLock = false;
  lastKeyPressed: string;
  showPasswordIconEnabled = false;
  showPassword = false;
  private _type = 'text';
  private _ruleRegex: string;
  private _validationExternalErrorText: string;

  constructor(
    private validationService: ValidationService,
    private fieldService: FieldService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.setLogic();
  }

  get type(): string {
    return this._type;
  }

  @Input()
  set type(val: string) {
    this._type = val || 'text';
    if (this.type === 'password') {
      this.showPasswordIconEnabled = true;
    }
  }

  get validationExternalErrorText(): string {
    return this._validationExternalErrorText;
  }

  // Validate form on validationExternalErrorText change.
  @Input()
  set validationExternalErrorText(val: string) {
    this._validationExternalErrorText = val;
    setTimeout(() => {
      this.formModel.control.updateValueAndValidity();
    });
  }

  get ruleRegex(): string {
    return this._ruleRegex;
  }

  // Regex to validate the model.
  @Input()
  set ruleRegex(val: string) {
    this._ruleRegex = val;
    this.validateRegex(this.model);
  }

  setLogic(): void {
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
    this.updateNgModelOnBlur = this.updateNgModelOnBlur ?? this.fieldService.getInputUpdateNgModelOnBlur();
  }

  onChangeModel(model: any): void {
    if (this.lastKeyPressed === keyboardKeys.DOT && this.type === 'number') {
      return;
    }

    // Back webservices do not accept empty model.
    if (model === '') {
      model = undefined;
    }

    if (this.type === 'number') {
      if (model !== undefined && model !== null) {
        // Force change detection
        this.model = null;
        this.changeDetectorRef.detectChanges();
        if (model % 1 === 0) {
          model = this.checkMinMaxModel(parseInt(model, 10));
        } else {
          model = this.checkMinMaxModel(parseFloat(model));
        }
      }

      this.model = model;
      this.modelChange.emit(model);
    } else {
      this.validateRegex(model);
      this.modelChange.emit(model);
    }
  }

  onClickArrow(addFlag: boolean): void {
    if (this.model || this.model === 0) {
      if (addFlag) {
        this.model += 1;
        this.onChangeModel(this.model);
      } else {
        this.model -= 1;
        this.onChangeModel(this.model);
      }
    } else {
      this.model = this.min || 0;
      this.onChangeModel(this.model);
    }

    this.markAsDirty();
  }

  markAsDirty(): void {
    this.formModel.control.markAsDirty();
  }

  checkMinMaxModel(number: number): number {
    if (!number && number !== 0) {
      return number;
    }

    if ((this.min || this.min === 0) && number < this.min) {
      return this.min;
    }

    if ((this.max || this.max === 0) && number > this.max) {
      return this.max;
    }

    return number;
  }

  validateRegex(model: string): void {
    if (model && this.ruleRegex) {
      const regex = new RegExp(this.ruleRegex);
      this.validationExternalErrorText = regex.test(model) ? null : this.ruleRegexErrorText;
    } else {
      this.validationExternalErrorText = null;
    }

    setTimeout(() => {
      this.formModel.control.updateValueAndValidity();
    });
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }

  onClickShowPassword(event: any): void {
    event.preventDefault();
    this.showPassword = !this.showPassword;
    this._type = this.showPassword ? 'text' : 'password';
  }

  @HostListener('keydown', ['$event'])
  async keyEvent(event: KeyboardEvent) {
    this.lastKeyPressed = event.key;

    if (this.type === 'password') {
      this.capsLock = event.getModifierState && event.getModifierState(keyboardKeys.CAPS_LOCK);
    }

    if (event.key === keyboardKeys.ENTER) {
      if (this.updateNgModelOnBlur) {
        (event.target as HTMLElement).blur();
        this.keyEnterPress.emit();
      } else {
        this.keyEnterPress.emit();
        setTimeout(() => {
          (event.target as HTMLElement).blur();
        });
      }
    }

    // If type is number, control if the user modifies the model using the arrow keys or it´s a negative number
    if (this.type === 'number' && (event.key === keyboardKeys.ARROW_DOWN || event.key === keyboardKeys.ARROW_UP || event.key === keyboardKeys.NUM_PAD_SUBTRACT)) {
      if (event.key === keyboardKeys.ARROW_DOWN && (this.min || this.min == 0) && this.model == this.min
        || event.key === keyboardKeys.ARROW_UP && (this.max || this.max == 0) && this.model == this.max
        || event.key === keyboardKeys.NUM_PAD_SUBTRACT && this.min >= 0) {
        event.preventDefault();
        return;
      }
    }
  }

  @HostListener('mousewheel', ['$event'])
  onMousewheel(event: any) {
    if (event.wheelDelta > 0 && this.type === 'number' && (this.max || this.max == 0) && this.model == this.max
      || event.wheelDelta < 0 && this.type === 'number' && (this.min || this.min == 0) && this.model == this.min) {
      event.preventDefault();
      return;
    }
  }
}
